import { db, auth } from './firebase'
import {
  collection,
  doc,
  addDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  limit,
  onSnapshot,
  serverTimestamp,
  arrayUnion,
  arrayRemove,
  getDoc,
  getDocs,
  increment
} from 'firebase/firestore'
import { hasMinimumRole, USER_ROLES } from './roleService'
import { checkRateLimit } from '../utils/rateLimiter'

// Colecciones
const CHATS_COLLECTION = 'chats'
const MESSAGES_COLLECTION = 'messages'

// Tipos de chat
export const CHAT_TYPES = {
  GENERAL_ES: 'general_es',
  GENERAL_EN: 'general_en',
  PUBLIC: 'public',      // Chat público personalizado
  PRIVATE: 'private',
  GROUP: 'group'
}

// Crear un nuevo chat
export const createChat = async (chatData) => {
  try {
    console.log('🆕 Creating chat with data:', chatData)

    // Check rate limit
    const { currentUser } = auth
    if (!currentUser) {
      throw new Error('Debes estar autenticado para crear chats')
    }

    const canProceed = await checkRateLimit(currentUser.uid, 'createChat')
    if (!canProceed) {
      throw new Error('Has excedido el límite de creación de chats. Por favor, espera unos minutos antes de intentar de nuevo.')
    }

    const chatRef = collection(db, CHATS_COLLECTION)

    // Calcular fecha de expiración (24 horas después de creación)
    const expiresAt = new Date()
    expiresAt.setHours(expiresAt.getHours() + 24)

    const newChat = {
      ...chatData,
      type: chatData.type || CHAT_TYPES.PRIVATE,
      participants: chatData.participants || [],
      messages: [],
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
      lastMessage: null,
      isActive: true,
      expiresAt: expiresAt // Fecha de expiración: 24 horas
    }

    console.log('📝 Adding document to Firestore...')
    const docRef = await addDoc(chatRef, newChat)
    console.log('✅ Chat creado con ID:', docRef.id, '- Expira en 24h')
    return docRef.id
  } catch (error) {
    console.error('❌ Error al crear chat:', error)
    console.error('❌ Error details:', error.code, error.message)

    // Manejo específico para errores de red/ad-blocker
    if (error.message && error.message.includes('ERR_BLOCKED_BY_CLIENT')) {
      throw new Error('BLOQUEADO: Tu ad-blocker está bloqueando Firebase. Desactívalo temporalmente y vuelve a intentarlo.')
    }

    // Manejo específico para errores de permisos
    if (error.code === 'permission-denied') {
      throw new Error('PERMISO: No tienes permisos para crear chats. Inicia sesión correctamente.')
    }

    // Manejo específico para errores de conexión
    if (error.code === 'unavailable' || error.code === 'deadline-exceeded') {
      throw new Error('CONEXIÓN: Problema de conexión a Firebase. Revisa tu internet e intenta de nuevo.')
    }

    // Error genérico
    throw new Error(`Error al crear chat: ${error.message || 'Error desconocido'}`)
  }
}

// Obtener chats disponibles para un usuario
export const getUserChats = async (userId) => {
  try {
    console.log('🔍 Getting chats for user:', userId)
    const chatsRef = collection(db, CHATS_COLLECTION)

    // Consulta simple sin orderBy para evitar necesidad de índice compuesto
    const q = query(
      chatsRef,
      where('participants', 'array-contains', userId)
    )

    const querySnapshot = await getDocs(q)
    console.log(`📊 Found ${querySnapshot.docs.length} docs in query`)

    const chats = []
    querySnapshot.forEach((doc) => {
      const chatData = {
        id: doc.id,
        ...doc.data()
      }
      // Filtrar por isActive y ordenar por updatedAt en el cliente
      if (chatData.isActive !== false) {
        chats.push(chatData)
      }
    })

    // Ordenar por updatedAt en el cliente (más recientes primero)
    chats.sort((a, b) => {
      const aTime = a.updatedAt?.toMillis ? a.updatedAt.toMillis() : (a.updatedAt || 0)
      const bTime = b.updatedAt?.toMillis ? b.updatedAt.toMillis() : (b.updatedAt || 0)
      return bTime - aTime
    })

    console.log(`✅ Returning ${chats.length} active chats for user`)
    return chats
  } catch (error) {
    console.error('❌ Error al obtener chats del usuario:', error)
    console.error('❌ Error details:', error.code, error.message)
    // Retornar array vacío en caso de error para no romper la UI
    return []
  }
}

// Obtener todos los chats públicos personalizados
export const getPublicChats = async (searchTerm = '') => {
  try {
    console.log('🔍 Getting all public chats...', searchTerm ? `with search: "${searchTerm}"` : '')
    const chatsRef = collection(db, CHATS_COLLECTION)

    const q = query(
      chatsRef,
      where('type', '==', CHAT_TYPES.PUBLIC),
      where('isActive', '==', true)
    )

    const querySnapshot = await getDocs(q)
    console.log(`📊 Found ${querySnapshot.docs.length} public chats`)

    const chats = []
    querySnapshot.forEach((doc) => {
      const chatData = {
        id: doc.id,
        ...doc.data()
      }

      // Filtrar por término de búsqueda si se proporciona
      if (searchTerm) {
        const searchLower = searchTerm.toLowerCase()
        const matchesName = chatData.name?.toLowerCase().includes(searchLower)
        const matchesDesc = chatData.description?.toLowerCase().includes(searchLower)

        if (matchesName || matchesDesc) {
          chats.push(chatData)
        }
      } else {
        chats.push(chatData)
      }
    })

    // Ordenar por updatedAt (más recientes primero)
    chats.sort((a, b) => {
      const aTime = a.updatedAt?.toMillis ? a.updatedAt.toMillis() : (a.updatedAt || 0)
      const bTime = b.updatedAt?.toMillis ? b.updatedAt.toMillis() : (b.updatedAt || 0)
      return bTime - aTime
    })

    console.log(`✅ Returning ${chats.length} public chats`)
    return chats
  } catch (error) {
    console.error('❌ Error al obtener chats públicos:', error)
    console.error('❌ Error details:', error.code, error.message)
    return []
  }
}

// Actualizar información de un chat
export const updateChat = async (chatId, updateData, userId) => {
  try {
    const chatRef = doc(db, CHATS_COLLECTION, chatId)
    const chatDoc = await getDoc(chatRef)

    if (!chatDoc.exists()) {
      throw new Error('Chat no encontrado')
    }

    const chat = chatDoc.data()

    // 1. Verificar si es admin del sistema (bypass para administradores)
    const isAdmin = await hasMinimumRole(userId, USER_ROLES.ADMIN)

    // 2. Si no es admin, verificar que es el creador del chat
    if (!isAdmin && chat.createdBy !== userId) {
      throw new Error('Solo el creador del chat puede editarlo')
    }

    const dataToUpdate = {
      ...updateData,
      updatedAt: serverTimestamp()
    }

    await updateDoc(chatRef, dataToUpdate)
    console.log('✅ Chat actualizado:', chatId)
    return true
  } catch (error) {
    console.error('❌ Error al actualizar chat:', error)
    throw error
  }
}

// Eliminar un chat (soft delete)
export const deleteChat = async (chatId, userId) => {
  try {
    const chatRef = doc(db, CHATS_COLLECTION, chatId)
    const chatDoc = await getDoc(chatRef)

    if (!chatDoc.exists()) {
      throw new Error('Chat no encontrado')
    }

    const chat = chatDoc.data()

    // 1. Verificar si es admin del sistema (bypass para administradores)
    const isAdmin = await hasMinimumRole(userId, USER_ROLES.ADMIN)

    // 2. Si no es admin, verificar que es el creador del chat
    if (!isAdmin && chat.createdBy !== userId) {
      throw new Error('Solo el creador del chat puede eliminarlo')
    }

    // Soft delete: marcar como inactivo
    await updateDoc(chatRef, {
      isActive: false,
      deletedAt: serverTimestamp(),
      deletedBy: userId,
      updatedAt: serverTimestamp()
    })

    console.log('✅ Chat eliminado:', chatId)
    return true
  } catch (error) {
    console.error('❌ Error al eliminar chat:', error)
    throw error
  }
}

// Reportar un chat inapropiado
export const reportChat = async (chatId, reporterId, reason) => {
  try {
    const reportsRef = collection(db, 'chat_reports')
    const report = {
      chatId,
      reporterId,
      reason,
      status: 'pending', // pending, reviewed, resolved, dismissed
      createdAt: serverTimestamp(),
      reviewedAt: null,
      reviewedBy: null,
      resolution: null
    }

    const docRef = await addDoc(reportsRef, report)
    console.log('✅ Chat reportado:', docRef.id)

    // Incrementar contador de reportes en el chat
    const chatRef = doc(db, CHATS_COLLECTION, chatId)
    await updateDoc(chatRef, {
      reportsCount: increment(1),
      lastReportAt: serverTimestamp()
    })

    return docRef.id
  } catch (error) {
    console.error('❌ Error al reportar chat:', error)
    throw error
  }
}

// Obtener reportes de un chat (para moderadores)
export const getChatReports = async (chatId = null) => {
  try {
    const reportsRef = collection(db, 'chat_reports')
    let q

    if (chatId) {
      q = query(
        reportsRef,
        where('chatId', '==', chatId),
        orderBy('createdAt', 'desc')
      )
    } else {
      q = query(
        reportsRef,
        orderBy('createdAt', 'desc')
      )
    }

    const querySnapshot = await getDocs(q)
    const reports = []

    querySnapshot.forEach((doc) => {
      reports.push({
        id: doc.id,
        ...doc.data()
      })
    })

    return reports
  } catch (error) {
    console.error('❌ Error al obtener reportes:', error)
    throw error
  }
}

// Verificar si un usuario puede editar/eliminar un chat
export const canEditChat = (chat, userId) => {
  if (!chat || !userId) return false
  return chat.createdBy === userId
}

// Buscar chats por término
export const searchChats = async (searchTerm, chatType = null) => {
  try {
    console.log('🔍 Searching chats:', searchTerm)
    const chatsRef = collection(db, CHATS_COLLECTION)

    // Para búsquedas, obtenemos todos los chats activos y filtramos en el cliente
    // (Firestore no soporta búsqueda de texto completo eficiente)
    let q
    if (chatType) {
      q = query(
        chatsRef,
        where('type', '==', chatType),
        where('isActive', '==', true)
      )
    } else {
      q = query(
        chatsRef,
        where('isActive', '==', true),
        limit(50) // Limitar para no sobrecargar
      )
    }

    const querySnapshot = await getDocs(q)
    const searchLower = searchTerm.toLowerCase()
    const chats = []

    querySnapshot.forEach((doc) => {
      const chatData = {
        id: doc.id,
        ...doc.data()
      }

      // Buscar en nombre y descripción
      const matchesName = chatData.name?.toLowerCase().includes(searchLower)
      const matchesDesc = chatData.description?.toLowerCase().includes(searchLower)

      if (matchesName || matchesDesc) {
        chats.push(chatData)
      }
    })

    // Ordenar por relevancia (prioridad: nombre > descripción)
    chats.sort((a, b) => {
      const aNameMatch = a.name?.toLowerCase().includes(searchLower)
      const bNameMatch = b.name?.toLowerCase().includes(searchLower)

      if (aNameMatch && !bNameMatch) return -1
      if (!aNameMatch && bNameMatch) return 1
      return 0
    })

    console.log(`✅ Found ${chats.length} chats matching "${searchTerm}"`)
    return chats
  } catch (error) {
    console.error('❌ Error al buscar chats:', error)
    throw error
  }
}

// Obtener chat general español (público)
export const getGeneralSpanishChat = async () => {
  try {
    console.log('🔍 Searching for Spanish general chat...')
    const chatsRef = collection(db, CHATS_COLLECTION)
    const q = query(
      chatsRef,
      where('type', '==', CHAT_TYPES.GENERAL_ES),
      limit(1)
    )

    console.log('📤 Executing Firestore query...')
    const querySnapshot = await getDocs(q)
    console.log('📊 Query result - docs found:', querySnapshot.docs.length)

    if (querySnapshot.empty) {
      // Si no existe, crearlo
      console.log('🇪🇸 Creando Chat General Español...')
      const chatId = await createChat({
        name: 'Chat General Español',
        description: 'Chat principal de la comunidad Wyra en español. ¡Comparte tus experiencias, encuentra patinadores en tu zona y organiza rutas!',
        type: CHAT_TYPES.GENERAL_ES,
        participants: [], // Público, sin participantes específicos
        isPublic: true,
        isActive: true,
        createdBy: 'system'
      })
      console.log('✅ Chat General Español creado con ID:', chatId)
      return chatId
    } else {
      const chatId = querySnapshot.docs[0].id
      const chatData = querySnapshot.docs[0].data()
      console.log('ℹ️ Chat General Español encontrado con ID:', chatId)
      console.log('📋 Chat data:', { name: chatData.name, type: chatData.type })
      return chatId
    }
  } catch (error) {
    console.error('❌ Error al obtener chat general español:', error)
    console.error('❌ Error code:', error.code)
    console.error('❌ Error message:', error.message)
    throw error
  }
}

// Obtener chat general inglés (público)
export const getGeneralEnglishChat = async () => {
  try {
    console.log('🔍 Searching for English general chat...')
    const chatsRef = collection(db, CHATS_COLLECTION)
    const q = query(
      chatsRef,
      where('type', '==', CHAT_TYPES.GENERAL_EN),
      limit(1)
    )

    console.log('📤 Executing Firestore query...')
    const querySnapshot = await getDocs(q)
    console.log('📊 Query result - docs found:', querySnapshot.docs.length)

    if (querySnapshot.empty) {
      // Si no existe, crearlo
      console.log('🇬🇧 Creating General English Chat...')
      const chatId = await createChat({
        name: 'General English Chat',
        description: 'Main Wyra community chat in English. Share your experiences, find skaters in your area, and organize routes!',
        type: CHAT_TYPES.GENERAL_EN,
        participants: [], // Público, sin participantes específicos
        isPublic: true,
        isActive: true,
        createdBy: 'system'
      })
      console.log('✅ General English Chat created with ID:', chatId)
      return chatId
    } else {
      const chatId = querySnapshot.docs[0].id
      const chatData = querySnapshot.docs[0].data()
      console.log('ℹ️ General English Chat found with ID:', chatId)
      console.log('📋 Chat data:', { name: chatData.name, type: chatData.type })
      return chatId
    }
  } catch (error) {
    console.error('❌ Error al obtener chat general inglés:', error)
    console.error('❌ Error code:', error.code)
    console.error('❌ Error message:', error.message)
    throw error
  }
}

// Enviar un mensaje
export const sendMessage = async (chatId, messageData) => {
  try {
    const { currentUser } = auth
    console.log('🔐 Auth check:', {
      hasAuth: !!auth,
      hasCurrentUser: !!currentUser,
      uid: currentUser?.uid,
      email: currentUser?.email
    })

    if (!currentUser) {
      throw new Error('Debes estar autenticado para enviar mensajes')
    }

    // Check rate limit
    const canProceed = await checkRateLimit(currentUser.uid, 'sendMessage')
    if (!canProceed) {
      throw new Error('Has excedido el límite de mensajes. Por favor, espera unos momentos antes de continuar.')
    }

    // Crear el mensaje con estructura mejorada del proyecto malo
    const messagesRef = collection(db, MESSAGES_COLLECTION)
    const message = {
      chatId,
      senderId: currentUser.uid,
      senderName: currentUser.displayName || 'Usuario',
      senderPhoto: currentUser.photoURL || null,
      text: messageData.text,
      type: messageData.type || 'text', // text, image, system
      createdAt: serverTimestamp(),
      edited: false,
      editedAt: null,
      reactions: [], // Array de reacciones {emoji, count, users: [uid1, uid2]}
      readBy: [currentUser.uid], // El que envía lo marca como leído
      replyTo: messageData.replyTo || null, // Para responder a mensajes
      attachments: messageData.attachments || [],
      reports: [], // Array de reportes {reporterId, reason, timestamp}
      reportsCount: 0
    }

    console.log('📝 Enviando mensaje:', {
      chatId,
      senderId: message.senderId,
      text: message.text
    })

    const messageDoc = await addDoc(messagesRef, message)

    // Actualizar el chat con el último mensaje
    const chatRef = doc(db, CHATS_COLLECTION, chatId)
    await updateDoc(chatRef, {
      lastMessage: {
        text: messageData.text,
        senderName: currentUser.displayName || 'Usuario',
        timestamp: serverTimestamp()
      },
      updatedAt: serverTimestamp()
    })

    console.log('Mensaje enviado:', messageDoc.id)
    return messageDoc.id
  } catch (error) {
    console.error('Error al enviar mensaje:', error)
    throw error
  }
}

// Editar un mensaje
export const editMessage = async (messageId, newText) => {
  try {
    const { currentUser } = auth
    if (!currentUser) {
      throw new Error('Debes estar autenticado para editar mensajes')
    }

    const messageRef = doc(db, MESSAGES_COLLECTION, messageId)
    await updateDoc(messageRef, {
      text: newText,
      edited: true,
      editedAt: serverTimestamp()
    })

    console.log('Mensaje editado:', messageId)
    return true
  } catch (error) {
    console.error('Error al editar mensaje:', error)
    throw error
  }
}

// Agregar reacción a mensaje
export const addReaction = async (messageId, emoji, userId) => {
  try {
    const messageRef = doc(db, MESSAGES_COLLECTION, messageId)
    const messageDoc = await getDoc(messageRef)

    if (!messageDoc.exists()) {
      throw new Error('Mensaje no encontrado')
    }

    const message = messageDoc.data()
    const existingReaction = message.reactions.find(r => r.emoji === emoji)

    let newReactions
    if (existingReaction) {
      // Si la reacción ya existe, remover usuario si ya estaba, o agregar si no estaba
      if (existingReaction.users.includes(userId)) {
        // Remover reacción del usuario
        newReactions = message.reactions
          .map(r => r.emoji === emoji
            ? { ...r, count: r.count - 1, users: r.users.filter(uid => uid !== userId) }
            : r
          )
          .filter(r => r.count > 0) // Eliminar reacciones con count = 0
      } else {
        // Agregar usuario a reacción existente
        newReactions = message.reactions.map(r =>
          r.emoji === emoji
            ? { ...r, count: r.count + 1, users: [...r.users, userId] }
            : r
        )
      }
    } else {
      // Crear nueva reacción
      newReactions = [...message.reactions, {
        emoji,
        count: 1,
        users: [userId]
      }]
    }

    await updateDoc(messageRef, {
      reactions: newReactions
    })

    console.log('Reacción agregada:', messageId, emoji)
    return true
  } catch (error) {
    console.error('Error al agregar reacción:', error)
    throw error
  }
}

// Marcar mensaje como leído
export const markMessageAsRead = async (messageId, userId) => {
  try {
    const messageRef = doc(db, MESSAGES_COLLECTION, messageId)
    await updateDoc(messageRef, {
      readBy: arrayUnion(userId)
    })
    return true
  } catch (error) {
    console.error('Error al marcar mensaje como leído:', error)
    throw error
  }
}

// Eliminación suave de mensaje (marca como eliminado en lugar de borrar)
export const softDeleteMessage = async (messageId) => {
  try {
    const { currentUser } = auth
    if (!currentUser) {
      throw new Error('Debes estar autenticado para eliminar mensajes')
    }

    const messageRef = doc(db, MESSAGES_COLLECTION, messageId)
    await updateDoc(messageRef, {
      text: 'Mensaje eliminado',
      type: 'deleted',
      deletedAt: serverTimestamp(),
      deletedBy: currentUser.uid
    })

    console.log('Mensaje eliminado suavemente:', messageId)
    return true
  } catch (error) {
    console.error('Error al eliminar mensaje:', error)
    throw error
  }
}

// Eliminar automáticamente mensajes de chats privados después de 24 horas
export const cleanupOldMessages = async () => {
  try {
    console.log('🧹 Iniciando limpieza de mensajes antiguos...')
    const messagesRef = collection(db, MESSAGES_COLLECTION)
    const now = new Date()
    const twentyFourHoursAgo = new Date(now.getTime() - 24 * 60 * 60 * 1000)

    // Obtener chats privados
    const chatsRef = collection(db, CHATS_COLLECTION)
    const privateChatsQuery = query(
      chatsRef,
      where('type', 'in', [CHAT_TYPES.PRIVATE, CHAT_TYPES.GROUP]),
      where('isActive', '==', true)
    )

    const privateChatsSnapshot = await getDocs(privateChatsQuery)
    const privateChatIds = privateChatsSnapshot.docs.map(doc => doc.id)

    if (privateChatIds.length === 0) {
      console.log('✅ No hay chats privados para limpiar')
      return 0
    }

    // Limitar a 10 chat IDs a la vez para evitar errores de "in" query
    const batchSize = 10
    let totalDeleted = 0

    for (let i = 0; i < privateChatIds.length; i += batchSize) {
      const batch = privateChatIds.slice(i, i + batchSize)

      try {
        // Eliminar mensajes antiguos de chats privados
        const oldMessagesQuery = query(
          messagesRef,
          where('chatId', 'in', batch),
          where('createdAt', '<', twentyFourHoursAgo),
          limit(100) // Limitar a 100 mensajes por batch
        )

        const oldMessagesSnapshot = await getDocs(oldMessagesQuery)

        if (oldMessagesSnapshot.empty) {
          console.log(`✅ Batch ${i / batchSize + 1}: No hay mensajes antiguos`)
          continue
        }

        // Eliminar documentos en batches para evitar errores de too many deletes
        const deletePromises = oldMessagesSnapshot.docs.map(doc =>
          deleteDoc(doc.ref).catch(err => {
            console.warn(`⚠️ Error eliminando mensaje ${doc.id}:`, err.message)
            return null
          })
        )

        await Promise.all(deletePromises)
        const deletedCount = oldMessagesSnapshot.docs.length
        totalDeleted += deletedCount
        console.log(`✅ Batch ${i / batchSize + 1}: ${deletedCount} mensajes eliminados`)
      } catch (batchError) {
        console.error(`❌ Error en batch ${i / batchSize + 1}:`, batchError.message)
        // Continuar con siguiente batch
      }
    }

    console.log(`✅ Limpieza completada: ${totalDeleted} mensajes eliminados`)
    return totalDeleted
  } catch (error) {
    console.error('❌ Error en limpieza de mensajes antiguos:', error)
    // No lanzar error para que el proceso continúe
    return 0
  }
}

// Eliminar automáticamente chats expirados (después de 24h)
export const deleteExpiredChats = async () => {
  try {
    console.log('🧹 Buscando chats expirados para eliminar...')
    const chatsRef = collection(db, CHATS_COLLECTION)
    const now = new Date()

    // Obtener todos los chats que tienen expiresAt
    const q = query(
      chatsRef,
      where('isActive', '==', true)
    )

    const querySnapshot = await getDocs(q)
    let deletedCount = 0

    for (const docSnapshot of querySnapshot.docs) {
      const chat = docSnapshot.data()

      // Verificar si el chat tiene expiresAt y ha expirado
      if (chat.expiresAt) {
        const expiresAtDate = chat.expiresAt.toDate ? chat.expiresAt.toDate() : new Date(chat.expiresAt)

        if (expiresAtDate < now) {
          // Soft delete del chat expirado
          await updateDoc(docSnapshot.ref, {
            isActive: false,
            deletedAt: serverTimestamp(),
            deletedBy: 'system_auto',
            deletionReason: 'expired_24h'
          })
          deletedCount++
          console.log(`🗑️ Chat expirado eliminado: ${docSnapshot.id}`)
        }
      }
    }

    console.log(`✅ Eliminados ${deletedCount} chats expirados`)
    return deletedCount
  } catch (error) {
    console.error('❌ Error al eliminar chats expirados:', error)
    throw error
  }
}

// Programar limpieza automática cada hora
export const scheduleCleanup = () => {
  // Ejecutar cleanup inmediatamente y luego cada hora
  cleanupOldMessages().catch(console.error)
  deleteExpiredChats().catch(console.error)

  setInterval(() => {
    cleanupOldMessages().catch(console.error)
    deleteExpiredChats().catch(console.error)
  }, 60 * 60 * 1000) // 1 hora en milisegundos
}

// Inicializar chats generales (crearlos si no existen)
export const initializeGeneralChats = async () => {
  try {
    console.log('🔍 Initializing general chats...')

    // Crear ambos chats generales si no existen
    const spanishChatId = await getGeneralSpanishChat()
    const englishChatId = await getGeneralEnglishChat()

    console.log('🎉 General chats initialized successfully!')
    console.log(`🇪🇸 Spanish Chat ID: ${spanishChatId}`)
    console.log(`🇬🇧 English Chat ID: ${englishChatId}`)

    return {
      spanishChatId,
      englishChatId
    }
  } catch (error) {
    console.error('❌ Error initializing general chats:', error)
    throw error
  }
}

// Obtener mensajes de un chat en tiempo real (con paginación optimizada para costos)
export const getChatMessages = (chatId, callback, options = {}) => {
  try {
    console.log(`🔍 Setting up message listener for chat: ${chatId}`)
    const messagesRef = collection(db, MESSAGES_COLLECTION)
    const pageSize = options.limit || 30 // Por defecto 30 mensajes para optimizar costos

    // Consulta optimizada con limit para reducir reads
    const q = query(
      messagesRef,
      where('chatId', '==', chatId),
      orderBy('createdAt', 'desc'),
      limit(pageSize)
    )

    return onSnapshot(q, (querySnapshot) => {
      const messages = []
      querySnapshot.forEach((doc) => {
        const messageData = doc.data()
        // Ensure backward compatibility with old message structure
        const message = {
          id: doc.id,
          text: messageData.text || '',
          senderId: messageData.senderId || '',
          senderName: messageData.senderName || 'Usuario',
          senderPhoto: messageData.senderPhoto || null,
          type: messageData.type || 'text',
          createdAt: messageData.createdAt || null,
          // New fields with defaults for old messages
          edited: messageData.edited || false,
          editedAt: messageData.editedAt || null,
          reactions: messageData.reactions || [],
          readBy: messageData.readBy || [],
          replyTo: messageData.replyTo || null,
          attachments: messageData.attachments || [],
          reports: messageData.reports || [],
          reportsCount: messageData.reportsCount || 0,
          deletedAt: messageData.deletedAt || null,
          deletedBy: messageData.deletedBy || null
        }
        messages.push(message)
      })

      // Ordenar por timestamp en orden ascendente (más antiguo primero) para el chat
      messages.sort((a, b) => {
        if (!a.createdAt) return 1
        if (!b.createdAt) return -1

        const aTime = a.createdAt.toMillis ? a.createdAt.toMillis() : a.createdAt
        const bTime = b.createdAt.toMillis ? b.createdAt.toMillis() : b.createdAt

        return aTime - bTime
      })

      console.log(`✅ Messages received for chat ${chatId}:`, messages.length, `(limited to ${pageSize})`)
      callback(messages)
    }, (error) => {
      console.error('❌ Error en listener de mensajes:', error)
      console.error('❌ Error details:', error.code, error.message)

      // Check for index requirement error
      if (error.message.includes('requires an index')) {
        console.warn('⚠️ This query requires an index. You have two options:')
        console.warn('⚠️ 1. Create the index at:', error.message.match(/https:\/\/[^s]+/)?.[0])
        console.warn('⚠️ 2. Continue with client-side ordering (current approach)')
      }

      // Importante: Llamar al callback con array vacío en caso de error de permisos
      // para que la UI se actualice aunque no se puedan cargar los mensajes
      if (error.code === 'permission-denied' || error.code === 'unauthenticated') {
        console.warn('⚠️ Permission error - calling callback with empty array')
        callback([])
      }
    })
  } catch (error) {
    console.error('❌ Error al configurar listener de mensajes:', error)
    throw error
  }
}

// Obtener información de un chat
export const getChatInfo = async (chatId) => {
  try {
    const chatRef = doc(db, CHATS_COLLECTION, chatId)
    const chatDoc = await getDoc(chatRef)

    if (chatDoc.exists()) {
      return {
        id: chatDoc.id,
        ...chatDoc.data()
      }
    } else {
      throw new Error('Chat no encontrado')
    }
  } catch (error) {
    console.error('Error al obtener información del chat:', error)
    throw error
  }
}

// Reportar un mensaje
export const reportMessage = async (messageId, reason, reporterId) => {
  try {
    // Aquí podrías implementar un sistema de reportes
    // Por ahora, solo lo logueamos
    console.log('Mensaje reportado:', {
      messageId,
      reason,
      reporterId,
      timestamp: new Date()
    })

    // En el futuro, podrías guardar esto en una colección de reportes
    return true
  } catch (error) {
    console.error('Error al reportar mensaje:', error)
    throw error
  }
}

// Eliminar un mensaje (solo para administradores del sistema o moderadores del chat)
export const deleteMessage = async (messageId, userId) => {
  try {
    // 1. Verificar si es admin del sistema
    const isAdmin = await hasMinimumRole(userId, USER_ROLES.ADMIN)

    if (!isAdmin) {
      // 2. Si no es admin, verificar si es moderador del chat
      const messageRef = doc(db, MESSAGES_COLLECTION, messageId)
      const messageDoc = await getDoc(messageRef)

      if (!messageDoc.exists()) {
        throw new Error('Mensaje no encontrado')
      }

      const message = messageDoc.data()

      // Obtener el chat para verificar si es moderador
      const chatRef = doc(db, CHATS_COLLECTION, message.chatId)
      const chatDoc = await getDoc(chatRef)

      if (!chatDoc.exists()) {
        throw new Error('Chat no encontrado')
      }

      const chat = chatDoc.data()

      // Verificar si es moderador del chat o el creador del mensaje
      const isChatModerator = chat.moderators?.includes(userId) || chat.createdBy === userId
      const isMessageCreator = message.senderId === userId

      if (!isChatModerator && !isMessageCreator) {
        throw new Error('No tienes permisos para eliminar este mensaje')
      }
    }

    // Eliminar el mensaje
    const messageRef = doc(db, MESSAGES_COLLECTION, messageId)
    await deleteDoc(messageRef)
    console.log('Mensaje eliminado:', messageId)
    return true
  } catch (error) {
    console.error('Error al eliminar mensaje:', error)
    throw error
  }
}

// Unirse a un chat privado
export const joinChat = async (chatId, userId) => {
  try {
    const chatRef = doc(db, CHATS_COLLECTION, chatId)
    await updateDoc(chatRef, {
      participants: arrayUnion(userId),
      updatedAt: serverTimestamp()
    })
    return true
  } catch (error) {
    console.error('Error al unirse al chat:', error)
    throw error
  }
}

// Abandonar un chat privado
export const leaveChat = async (chatId, userId) => {
  try {
    const chatRef = doc(db, CHATS_COLLECTION, chatId)
    await updateDoc(chatRef, {
      participants: arrayRemove(userId),
      updatedAt: serverTimestamp()
    })
    return true
  } catch (error) {
    console.error('Error al abandonar el chat:', error)
    throw error
  }
}

// ========= FUNCIONES PARA CHATS GRUPALES =========

// Crear chat para un grupo
export const createGroupChat = async (groupId, groupName, creatorId) => {
  try {
    console.log('🆕 Creating group chat for:', { groupId, groupName, creatorId })

    const chatData = {
      name: `${groupName} - Chat`,
      description: `Chat del grupo ${groupName}`,
      type: CHAT_TYPES.GROUP,
      groupId: groupId,
      createdBy: creatorId,
      participants: [creatorId], // El creador se añade automáticamente
      isActive: true,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    }

    const chatRef = await addDoc(collection(db, CHATS_COLLECTION), chatData)
    console.log('✅ Group chat created with ID:', chatRef.id)
    return chatRef.id
  } catch (error) {
    console.error('❌ Error creating group chat:', error)
    throw error
  }
}

// Obtener chat de un grupo específico
export const getGroupChat = async (groupId) => {
  try {
    const chatsRef = collection(db, CHATS_COLLECTION)
    const q = query(
      chatsRef,
      where('groupId', '==', groupId),
      where('type', '==', CHAT_TYPES.GROUP),
      where('isActive', '==', true),
      limit(1)
    )

    const querySnapshot = await getDocs(q)

    if (!querySnapshot.empty) {
      const chatDoc = querySnapshot.docs[0]
      return {
        id: chatDoc.id,
        ...chatDoc.data()
      }
    }

    // Si no existe el chat, crear uno nuevo
    console.log('🔄 Group chat not found, creating new one...')
    return null // El llamador debe crear uno nuevo si es necesario
  } catch (error) {
    console.error('❌ Error getting group chat:', error)
    throw error
  }
}

// Obtener o crear chat de grupo (función de conveniencia)
export const getOrCreateGroupChat = async (groupId, groupName, creatorId) => {
  try {
    // Primero intentar obtener el chat existente
    let groupChat = await getGroupChat(groupId)

    if (!groupChat) {
      // Si no existe, crear uno nuevo
      console.log('🆕 Creating new group chat...')
      const chatId = await createGroupChat(groupId, groupName, creatorId)

      // Obtener el chat recién creado
      groupChat = await getGroupChat(groupId)
    }

    return groupChat
  } catch (error) {
    console.error('❌ Error getting or creating group chat:', error)
    throw error
  }
}

// Unir usuario al chat del grupo (cuando se une al grupo)
export const addUserToGroupChat = async (groupId, userId) => {
  try {
    const groupChat = await getGroupChat(groupId)
    if (!groupChat) {
      throw new Error('El chat del grupo no existe')
    }

    const chatRef = doc(db, CHATS_COLLECTION, groupChat.id)
    await updateDoc(chatRef, {
      participants: arrayUnion(userId),
      updatedAt: serverTimestamp()
    })

    console.log('✅ User added to group chat:', { groupId, userId })
    return true
  } catch (error) {
    console.error('❌ Error adding user to group chat:', error)
    throw error
  }
}

// Eliminar usuario del chat del grupo (cuando sale del grupo)
export const removeUserFromGroupChat = async (groupId, userId) => {
  try {
    const groupChat = await getGroupChat(groupId)
    if (!groupChat) {
      throw new Error('El chat del grupo no existe')
    }

    const chatRef = doc(db, CHATS_COLLECTION, groupChat.id)
    await updateDoc(chatRef, {
      participants: arrayRemove(userId),
      updatedAt: serverTimestamp()
    })

    console.log('✅ User removed from group chat:', { groupId, userId })
    return true
  } catch (error) {
    console.error('❌ Error removing user from group chat:', error)
    throw error
  }
}

// Verificar si usuario tiene acceso al chat del grupo
export const canAccessGroupChat = async (groupId, userId) => {
  try {
    const groupChat = await getGroupChat(groupId)
    if (!groupChat) {
      return false
    }

    return groupChat.participants && groupChat.participants.includes(userId)
  } catch (error) {
    console.error('❌ Error checking group chat access:', error)
    return false
  }
}

// Eliminar chat de grupo (cuando se elimina el grupo)
export const deleteGroupChat = async (groupId) => {
  try {
    const groupChat = await getGroupChat(groupId)
    if (!groupChat) {
      console.log('⚠️ Group chat not found, nothing to delete')
      return true
    }

    const chatRef = doc(db, CHATS_COLLECTION, groupChat.id)
    await updateDoc(chatRef, {
      isActive: false,
      updatedAt: serverTimestamp()
    })

    console.log('✅ Group chat deleted:', groupChat.id)
    return true
  } catch (error) {
    console.error('❌ Error deleting group chat:', error)
    throw error
  }
}

export default {
  createChat,
  getUserChats,
  getPublicChats,
  updateChat,
  deleteChat,
  reportChat,
  getChatReports,
  canEditChat,
  searchChats,
  getGeneralSpanishChat,
  getGeneralEnglishChat,
  initializeGeneralChats,
  sendMessage,
  editMessage,
  addReaction,
  markMessageAsRead,
  softDeleteMessage,
  cleanupOldMessages,
  deleteExpiredChats,
  scheduleCleanup,
  getChatMessages,
  getChatInfo,
  reportMessage,
  deleteMessage,
  joinChat,
  leaveChat,
  createGroupChat,
  getGroupChat,
  getOrCreateGroupChat,
  addUserToGroupChat,
  removeUserFromGroupChat,
  canAccessGroupChat,
  deleteGroupChat,
  CHAT_TYPES
}